// admin/src/pages/Deploy.jsx
import React, { useState, useRef } from 'react';
import TopNav from '../components/TopNav';
import { useAdminStore } from '../context/adminStore';

const API_ROOT = process.env.REACT_APP_API_URL || 'http://localhost:5000';
const QUIZ_BASE = process.env.REACT_APP_QUIZ_URL || 'http://localhost:3000';

export default function Deploy() {
  const { state } = useAdminStore();
  const tenantId = state.tenantId || 'demo-university';

  const [copiedEmbed, setCopiedEmbed] = useState(false);
  const [copiedUrl, setCopiedUrl] = useState(false);
  const embedRef = useRef(null);

  // Standalone URL
  const standaloneUrl = `${QUIZ_BASE}/?tenant=${encodeURIComponent(tenantId)}`;

  // Embed snippet
  const embedSnippet = `<div id="mwc-quiz"></div>\n<script src="${API_ROOT}/embed.js"\n        data-tenant="${tenantId}">\n</script>`;

  const copyToClipboard = async (text, setter) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.cssText = 'position:fixed;opacity:0';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    }
    setter(true);
    setTimeout(() => setter(false), 2500);
  };

  return (
    <>
      <TopNav />
      <div className="page-pad">
        <header style={{ marginBottom: 24 }}>
          <h1 style={{ margin: '0 0 8px 0' }}>🚀 Deploy Quiz</h1>
          <p style={{ margin: 0, color: '#6b7280', fontSize: '0.9rem' }}>
            Share the quiz as a standalone page or embed it on any website.
          </p>
        </header>

        {/* Tenant badge */}
        <div style={{
          display: 'inline-flex', alignItems: 'center', gap: 8,
          padding: '8px 14px', background: '#f0f9ff', border: '1px solid #bae6fd',
          borderRadius: 8, fontSize: '0.85rem', marginBottom: 28
        }}>
          <span style={{ fontWeight: 600, color: '#0369a1' }}>Tenant:</span>
          <code style={{
            background: '#e0f2fe', padding: '2px 8px', borderRadius: 4,
            fontFamily: 'monospace', color: '#0c4a6e'
          }}>
            {tenantId}
          </code>
        </div>

        {/* ── Standalone Landing Page ──────────────────────────────── */}
        <section className="adm-card" style={{ marginBottom: 24 }}>
          <div className="adm-card__header" style={{ border: 'none', paddingBottom: 0 }}>
            <div className="adm-card__headtext">
              <div className="adm-title" style={{ fontSize: '1.1rem' }}>
                🔗 Standalone Quiz Page
              </div>
              <div className="adm-sub" style={{ marginTop: 4 }}>
                Link directly to the quiz from your website, email campaigns, or social media.
              </div>
            </div>
          </div>

          <div className="adm-card__body" style={{ paddingTop: 16 }}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'stretch' }}>
              <input
                readOnly
                value={standaloneUrl}
                onClick={e => e.target.select()}
                style={{
                  flex: 1, padding: '10px 14px', fontFamily: 'monospace',
                  fontSize: '0.85rem', border: '1.5px solid #d1d5db', borderRadius: 8,
                  background: '#f9fafb', color: '#111827', outline: 'none'
                }}
              />
              <button
                className="btn btn--primary"
                onClick={() => copyToClipboard(standaloneUrl, setCopiedUrl)}
                style={{ whiteSpace: 'nowrap', minWidth: 100 }}
              >
                {copiedUrl ? '✓ Copied!' : 'Copy URL'}
              </button>
            </div>

            <div style={{ marginTop: 12, display: 'flex', gap: 12, alignItems: 'center' }}>
              <a
                href={standaloneUrl}
                target="_blank"
                rel="noopener noreferrer"
                style={{ fontSize: '0.85rem', color: '#2563eb', textDecoration: 'underline' }}
              >
                Open in new tab →
              </a>
            </div>

            <div style={{
              marginTop: 16, padding: '12px 16px', background: '#fffbeb',
              border: '1px solid #fde68a', borderRadius: 8, fontSize: '0.8rem', color: '#92400e'
            }}>
              <strong>Tip:</strong> For a clean URL, point a subdomain like{' '}
              <code style={{ background: '#fef3c7', padding: '1px 4px', borderRadius: 3 }}>
                discover.youruniversity.edu
              </code>{' '}
              to your quiz app's hosting and configure the tenant automatically via environment variables.
            </div>
          </div>
        </section>

        {/* ── Embed on Your Website ───────────────────────────────── */}
        <section className="adm-card" style={{ marginBottom: 24 }}>
          <div className="adm-card__header" style={{ border: 'none', paddingBottom: 0 }}>
            <div className="adm-card__headtext">
              <div className="adm-title" style={{ fontSize: '1.1rem' }}>
                📋 Embed on Your Website
              </div>
              <div className="adm-sub" style={{ marginTop: 4 }}>
                Paste this snippet into any HTML page. The quiz loads inside a Shadow DOM
                for full CSS isolation — no conflicts with your site's styles.
              </div>
            </div>
          </div>

          <div className="adm-card__body" style={{ paddingTop: 16 }}>
            <div style={{ position: 'relative' }}>
              <pre
                ref={embedRef}
                onClick={() => {
                  if (embedRef.current) {
                    const range = document.createRange();
                    range.selectNodeContents(embedRef.current);
                    const sel = window.getSelection();
                    sel.removeAllRanges();
                    sel.addRange(range);
                  }
                }}
                style={{
                  padding: 16, background: '#1e293b', color: '#e2e8f0', borderRadius: 8,
                  fontSize: '0.82rem',
                  fontFamily: "'SF Mono', 'Fira Code', 'Cascadia Code', Consolas, monospace",
                  lineHeight: 1.6, overflowX: 'auto', cursor: 'pointer', margin: 0,
                  whiteSpace: 'pre-wrap', wordBreak: 'break-all'
                }}
              >
                {embedSnippet}
              </pre>

              <button
                className="btn btn--primary"
                onClick={() => copyToClipboard(embedSnippet, setCopiedEmbed)}
                style={{ position: 'absolute', top: 8, right: 8, fontSize: '0.78rem', padding: '6px 12px' }}
              >
                {copiedEmbed ? '✓ Copied!' : 'Copy Code'}
              </button>
            </div>

            <div style={{ marginTop: 16 }}>
              <div style={{ fontSize: '0.85rem', fontWeight: 600, color: '#374151', marginBottom: 8 }}>
                How it works:
              </div>
              <ol style={{
                margin: 0, paddingLeft: 20, fontSize: '0.82rem', color: '#4b5563', lineHeight: 1.7
              }}>
                <li>
                  The <code style={{ background: '#f3f4f6', padding: '1px 4px', borderRadius: 3 }}>&lt;div&gt;</code> is
                  the container where the quiz will appear.
                </li>
                <li>
                  The script loads the quiz bundle and mounts it inside a{' '}
                  <strong>Shadow DOM</strong> — your page's CSS won't affect the quiz, and vice versa.
                </li>
                <li>
                  Works on any CMS: WordPress, Drupal, Squarespace, or custom HTML pages.
                </li>
              </ol>
            </div>

            <div style={{
              marginTop: 16, padding: '12px 16px', background: '#f0fdf4',
              border: '1px solid #bbf7d0', borderRadius: 8, fontSize: '0.8rem', color: '#166534'
            }}>
              <strong>Compatibility:</strong> Shadow DOM is supported in Chrome, Firefox, Safari, and Edge.
              No polyfills needed.
            </div>
          </div>
        </section>

        {/* ── Pre-Launch Checklist ────────────────────────────────── */}
        <section className="adm-card">
          <div className="adm-card__header" style={{ border: 'none', paddingBottom: 0 }}>
            <div className="adm-card__headtext">
              <div className="adm-title" style={{ fontSize: '1.1rem' }}>
                ✅ Pre-Launch Checklist
              </div>
            </div>
          </div>

          <div className="adm-card__body" style={{ paddingTop: 12 }}>
            <Checklist items={[
              'Questions imported and enabled (all 3 phases)',
              'Programs imported with areas of study tags',
              'Result content cards configured with mindset/content tags',
              'Mindset tags seeded (seed:tags)',
              'Pre-quiz form working (name, degree level, age range)',
              'Test a full quiz flow end-to-end',
              'Review session results in the Sessions tab',
              'Email capture endpoint responding (PATCH /api/sessions/:id/email)',
              'API server deployed and accessible from host domain',
              'CORS configured for production domain'
            ]} />
          </div>
        </section>
      </div>
    </>
  );
}

function Checklist({ items }) {
  const [checked, setChecked] = useState({});

  const toggle = (idx) => {
    setChecked(prev => ({ ...prev, [idx]: !prev[idx] }));
  };

  const doneCount = Object.values(checked).filter(Boolean).length;

  return (
    <div>
      <div style={{
        fontSize: '0.82rem', color: '#6b7280', marginBottom: 12, fontWeight: 500
      }}>
        {doneCount} of {items.length} complete
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {items.map((item, idx) => (
          <label
            key={idx}
            style={{
              display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer',
              padding: '6px 0',
              color: checked[idx] ? '#9ca3af' : '#374151',
              textDecoration: checked[idx] ? 'line-through' : 'none',
              fontSize: '0.85rem', transition: 'color 0.15s'
            }}
          >
            <input
              type="checkbox"
              checked={!!checked[idx]}
              onChange={() => toggle(idx)}
              style={{ width: 18, height: 18, cursor: 'pointer' }}
            />
            {item}
          </label>
        ))}
      </div>
    </div>
  );
}